import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import type { Plan } from '../types';

function priceLabel(p: Plan): string {
  if (p.billingType === 'free') return 'Free';
  const dollars = (p.priceCents / 100).toFixed(2);
  if (p.billingType === 'monthly') return `$${dollars}/mo`;
  return `$${dollars} once`;
}

export function UpgradePage() {
  const { user } = useAuth();
  const location = useLocation();
  const [plans, setPlans] = useState<Plan[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    void api<Plan[]>('/api/plans')
      .then(setPlans)
      .catch((err) => setError(err instanceof Error ? err.message : 'Load failed'));
  }, []);

  async function onChoose(plan: Plan) {
    setError(null); setInfo(null); setBusy(plan.slug);
    try {
      const res = await api<{ url: string | null; message?: string }>(
        '/api/checkout/session',
        { method: 'POST', body: JSON.stringify({ planSlug: plan.slug }) },
      );
      if (res.url) {
        window.location.href = res.url;
      } else {
        setInfo(res.message ?? 'Checkout is not available right now.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start checkout.');
    } finally {
      setBusy(null);
    }
  }

  const isCancelled = location.pathname.endsWith('/cancelled');
  const isSuccess = location.pathname.endsWith('/success');

  return (
    <div className="page-content">
      <header className="dashboard-header">
        <h1>Upgrade your plan</h1>
        <p>Unlock the rest of the categories.</p>
      </header>

      {isSuccess && (
        <div className="info-banner" role="status">
          Payment received — your plan should reflect within a few seconds. Refreshing…
        </div>
      )}
      {isCancelled && (
        <div className="info-banner" role="status">
          Checkout cancelled. Your plan is unchanged.
        </div>
      )}
      {info && <div className="info-banner" role="status">{info}</div>}
      {error && <div className="form-error" role="alert">{error}</div>}

      {!plans && !error && <p style={{ color: 'var(--text-muted)' }}>Loading plans…</p>}

      {plans && plans.length > 0 && (
        <div className="plan-grid">
          {plans.map((p) => {
            const current = user?.plan?.slug === p.slug;
            const free = p.billingType === 'free';
            return (
              <article key={p.id} className={`plan-card${current ? ' is-current' : ''}`}>
                <h2>{p.name}</h2>
                <p className="plan-price">{priceLabel(p)}</p>
                <p style={{ color: 'var(--text-secondary)' }}>{p.description}</p>
                <ul className="plan-features">
                  {p.categories.map((c) => (<li key={c.id}>{c.name}</li>))}
                </ul>
                {current ? (
                  <span className="badge badge-active">Your plan</span>
                ) : free ? (
                  <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>Default for new accounts</span>
                ) : (
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={() => void onChoose(p)}
                    disabled={busy !== null}
                  >
                    {busy === p.slug ? 'Starting checkout…' : `Choose ${p.name}`}
                  </button>
                )}
              </article>
            );
          })}
        </div>
      )}

      <p style={{ marginTop: 24 }}>
        <Link to="/" className="btn-link">← Back to dashboard</Link>
      </p>
    </div>
  );
}
